import { createRouter, createWebHistory } from 'vue-router'
import { routes } from 'vue-router/auto-routes'
import { DEFAULT_SPACE, isKnownSpace } from '@/constants/spaces'
import { isLocal } from '@/utils/env'
import { setDocumentTitle } from '@/composables/use-document-title'
import { getSpaceFromParams, useSpaces } from '@/composables/use-spaces'
import type { SpaceFeature } from '@/constants/spaces'
import type { Router } from 'vue-router'

export let router: Router

export const getRouter = (): Router => {
  router = createRouter({
    history: createWebHistory(),
    routes,
  })

  router.beforeEach((to) => {
    if (to.meta.space === undefined) return

    const { isSpaceAllowed } = useSpaces()
    const feature = to.meta.feature as SpaceFeature | undefined

    if (
      !isKnownSpace(to.meta.space) ||
      !isSpaceAllowed(to.meta.space, feature)
    ) {
      return { path: '/404', replace: true }
    }
  })

  router.beforeEach((to) => {
    if (!('space' in to.params) || !to.params.space) return

    if (to.params.space === DEFAULT_SPACE) {
      return {
        name: to.name,
        params: { ...to.params, space: '' },
        query: to.query,
        hash: to.hash,
        replace: true,
      }
    }

    const { isSpaceAllowed } = useSpaces()
    const space = getSpaceFromParams(to.params)
    const feature = to.meta.feature as SpaceFeature | undefined

    if (space !== to.params.space || !isSpaceAllowed(space, feature)) {
      // several catch-all routes share the name '404', so redirect by path
      return { path: '/404', replace: true }
    }
  })

  router.beforeEach((to) => {
    let title = ''

    if (to.meta.title) {
      title = to.meta.title as string
    } else if (to.meta.titleTemplate) {
      // if a template exists, swap placeholders with actual params
      title = (to.meta.titleTemplate as string).replace(
        '%id%',
        to.params.id as string,
      )
    }

    setDocumentTitle(title)
  })

  // Hard-reload the page when chunk load errors match the navigation error
  // https://github.com/vitejs/vite/issues/11804#issuecomment-1760951463
  router.onError((error, to) => {
    if (isLocal()) return

    const errors = [
      'Failed to fetch dynamically imported module',
      'Unable to preload CSS',
      'error loading dynamically imported module',
    ]

    if (errors.some((e) => error.message.includes(e))) {
      if (to?.fullPath) {
        window.location.href = to.fullPath
      } else {
        window.location.reload()
      }
    } else {
      // TODO: activate after setup sentry
      // captureException('Unknown router error.', error)
    }
  })

  return router
}
